import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import { AlertTriangle, ArrowLeft, ExternalLink, FileText, Loader2, MessageSquareText, Sparkles } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewCandidateCard from "components/plan-review-candidate-card/plan-review-candidate-card";

import { useGetPlanReviewExtractionSubscription } from "graphql/subscriptions/plan-review-extraction.generated";
import { useGetPlanReviewSubmissionTypesQuery } from "graphql/queries/get-plan-review-submission-types.generated";
import { usePlanReviewCategories } from "hooks/usePlanReviewCategories";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { useUpdatePlanReviewCandidateMutation } from "graphql/mutations/update-plan-review-candidate.generated";
import { useSetPlanReviewCandidatesResolutionMutation } from "graphql/mutations/set-plan-review-candidates-resolution.generated";
import { useSetPlanReviewCandidateSubmissionTypesMutation } from "graphql/mutations/set-plan-review-candidate-submission-types.generated";
import { useImportPlanReviewRequirementCandidatesMutation } from "graphql/mutations/import-plan-review-requirement-candidates.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import routeNames from "constants/route-names";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";
import {
	PlanReviewCandidateMatchKind,
	PlanReviewCandidateResolution,
	PlanReviewExtractionStatus
} from "constants/field-values";

type Extraction = NonNullable<
	NonNullable<
		ReturnType<typeof useGetPlanReviewExtractionSubscription>["data"]
	>["plan_review_requirement_extractions_by_pk"]
>;
type Candidate = Extraction["plan_review_requirement_candidates"][number];

const formatBytes = (bytes: number): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// What the reviewer needs to know about the file at a glance: its kind, from the
// extension when there is one, falling back to the mime type the upload reported.
const documentKind = (fileName: string, mimeType?: string | null): string => {
	const extension = fileName.split(".").pop()?.toUpperCase();
	if (extension && extension.length <= 5 && extension !== fileName.toUpperCase()) return extension;
	return mimeType?.split("/").pop()?.toUpperCase() ?? "Document";
};

/**
 * Step 2 of the AI extraction: the reviewer's desk.
 *
 * Every proposed rule shows the sentence it was read from and, when it collides with
 * a rule already on file, both versions side by side — the reviewer decides per rule
 * whether to skip it, replace what exists, or keep both.
 */
const PlanReviewRulesExtraction = () => {
	const navigate = useNavigate();
	const { extractionId } = useParams<{ extractionId: string }>();
	const { loggedUserValue } = useLoggedUser();
	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	// Appended to every control that writes: it reads as unavailable but still answers.
	const lockedClass = canManage ? "" : " prre-locked";

	const [isImporting, setIsImporting] = useState(false);

	const { data, loading } = useGetPlanReviewExtractionSubscription({
		skip: !extractionId,
		variables: { id: extractionId as string }
	});

	const extraction = data?.plan_review_requirement_extractions_by_pk;

	// The run's jurisdiction, not the reader's: a platform admin reviewing another
	// AHJ's extraction still has to see that AHJ's submission types.
	const jurisdictionId = extraction?.jurisdiction_id ?? "";

	const { data: typesData } = useGetPlanReviewSubmissionTypesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const submissionTypes = useMemo(() => typesData?.plan_review_submission_types ?? [], [typesData]);

	const { categoryOptions } = usePlanReviewCategories(jurisdictionId);

	const [updateCandidate] = useUpdatePlanReviewCandidateMutation();
	const [setResolutions] = useSetPlanReviewCandidatesResolutionMutation();
	const [setCandidateTypes] = useSetPlanReviewCandidateSubmissionTypesMutation();
	const [importCandidates] = useImportPlanReviewRequirementCandidatesMutation();

	const candidates = useMemo(() => extraction?.plan_review_requirement_candidates ?? [], [extraction]);
	const documents = useMemo(() => extraction?.plan_review_extraction_documents ?? [], [extraction]);
	// Tells Ollie which extraction run is on screen and how far along it is.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen: extraction
				? `One AI rule-extraction run: a document this authority published, read to propose the rules a reviewer will check submittals against. Its status is ${extraction.status}. It proposed ${candidates.length} candidate rules from ${documents.length} documents.`
				: "",
			ids: [["plan_review_requirement_extractions.id", extractionId]],
			jurisdictionId
		})
	);


	const counts = useMemo(
		() => ({
			total: candidates.length,
			new: candidates.filter(c => c.match_kind === PlanReviewCandidateMatchKind.NEW).length,
			changed: candidates.filter(c => c.match_kind === PlanReviewCandidateMatchKind.CHANGED).length,
			identical: candidates.filter(c => c.match_kind === PlanReviewCandidateMatchKind.IDENTICAL).length,
			// Still actionable: neither imported nor skipped. Counting every non-skipped rule
			// would make a second visit offer to import what is already in the container.
			pending: candidates.filter(c => !c.imported_requirement_id && c.resolution !== PlanReviewCandidateResolution.SKIP)
				.length
		}),
		[candidates]
	);

	const isCompleted = extraction?.status === PlanReviewExtractionStatus.COMPLETED;
	const isCancelled = extraction?.status === PlanReviewExtractionStatus.CANCELLED;
	// The reviewer's desk, whether or not the review is over: a completed run still shows
	// every candidate, because reopening one is how a skipped rule gets imported later.
	// A cancelled run shows them too, but only to be read.
	const isReviewable =
		extraction?.status === PlanReviewExtractionStatus.READY ||
		extraction?.status === PlanReviewExtractionStatus.COMPLETED ||
		isCancelled;

	const handleFieldChange = useCallback(
		async (candidate: { id: string }, field: string, value: unknown) => {
			try {
				await updateCandidate({ variables: { id: candidate.id, set: { [field]: value } } });
			} catch (error) {
				console.error("Candidate update failed:", error);
				message.error("We couldn't save that change. Please try again.");
			}
		},
		[updateCandidate]
	);

	const handleTypesChange = useCallback(
		async (candidate: { id: string }, ids: string[]) => {
			try {
				await setCandidateTypes({
					variables: {
						candidateId: candidate.id,
						objects: ids.map(submission_type_id => ({
							candidate_id: candidate.id,
							submission_type_id
						}))
					}
				});
			} catch (error) {
				console.error("Candidate submission types update failed:", error);
				message.error("We couldn't save the submission types. Please try again.");
			}
		},
		[setCandidateTypes]
	);

	const handleBulkResolution = useCallback(
		async (matchKind: string, resolution: string) => {
			if (!canManage) return blockChange();
			const ids = candidates.filter(c => c.match_kind === matchKind).map(c => c.id);
			if (ids.length === 0) return;
			try {
				await setResolutions({ variables: { ids, resolution } });
			} catch (error) {
				console.error("Bulk resolution failed:", error);
				message.error("We couldn't apply that to every rule. Please try again.");
			}
		},
		[candidates, setResolutions, canManage, blockChange]
	);

	const handleImport = useCallback(async () => {
		if (!canManage) return blockChange();
		if (!extractionId) return;
		setIsImporting(true);
		try {
			const result = await importCandidates({
				variables: {
					input: { extraction_id: extractionId, created_by_user_id: loggedUserValue?.id }
				}
			});
			const output = result.data?.import_plan_review_requirement_candidates;
			if (!output?.success) throw new Error(output?.error ?? "The rules couldn't be imported.");

			message.success(
				`${output.imported_count ?? 0} rules created, ${output.replaced_count ?? 0} replaced, ${
					output.skipped_count ?? 0
				} skipped.`
			);
			navigate(routeNames.PLAN_REVIEW_RULES);
		} catch (error) {
			console.error("Rule import failed:", error);
			message.error(error instanceof Error ? error.message : "The rules couldn't be imported.");
		} finally {
			setIsImporting(false);
		}
	}, [extractionId, importCandidates, loggedUserValue, navigate, canManage, blockChange]);

	if (loading && !extraction) return <LoadingOverlay />;

	if (!extraction) {
		return (
			<OllieLayout>
				<div className="prre-page">
					<div className="prre-shell">
						<div className="prre-empty">
							<p className="prre-empty-title">This extraction could not be found</p>
							<button className="prre-btn" onClick={() => navigate(routeNames.PLAN_REVIEW_RULES)}>
								Back to rules
							</button>
						</div>
					</div>
				</div>
			</OllieLayout>
		);
	}

	return (
		<OllieLayout>
			<div className="prre-page">
				<div className="prre-shell">
					<button className="prre-back" onClick={() => navigate(routeNames.PLAN_REVIEW_RULES)}>
						<ArrowLeft size={16} />
						<span>Back to rules</span>
					</button>

					<header className="prre-header">
						<div>
							<h1 className="prre-title">Review extracted rules</h1>
							<p className="prre-subtitle">Nothing is added to your rules container until you import.</p>
						</div>
						{isReviewable && !isCompleted ? (
							<div className="prre-header-actions">
								<button
									className={`prre-btn${lockedClass}`}
									aria-disabled={!canManage}
									onClick={() =>
										handleBulkResolution(PlanReviewCandidateMatchKind.IDENTICAL, PlanReviewCandidateResolution.SKIP)
									}
								>
									Skip unchanged
								</button>
								<button
									className={`prre-btn prre-btn-primary${lockedClass}`}
									onClick={handleImport}
									aria-disabled={!canManage}
									disabled={counts.pending === 0 || isImporting}
								>
									<Sparkles size={16} />
									Import {counts.pending} rules
								</button>
							</div>
						) : null}
					</header>

					{notice}

					{/* What the run read, openable — the reviewer checks a quote against its page */}
					{documents.length > 0 ? (
						<section className="prre-section">
							<h2 className="prre-section-title">
								<FileText size={18} className="prre-section-icon" />
								Source Documents
								<span className="prre-section-count">{documents.length}</span>
							</h2>
							<div className="prre-docs-grid">
								{documents.map(doc => (
									<a
										key={doc.id}
										href={doc.file_url}
										target="_blank"
										rel="noreferrer"
										className="prre-doc-card"
										title={`Open ${doc.file_name}`}
									>
										<div className="prre-doc-icon">
											<FileText size={18} />
										</div>
										<div className="prre-doc-info">
											<span className="prre-doc-name">{doc.file_name}</span>
											<span className="prre-doc-meta">
												{documentKind(doc.file_name, doc.mime_type)}
												{doc.file_size_bytes ? ` · ${formatBytes(Number(doc.file_size_bytes))}` : ""}
											</span>
										</div>
										<ExternalLink size={15} className="prre-doc-open" />
									</a>
								))}
							</div>
						</section>
					) : null}

					{/* The note that shaped this run, kept visible: it explains the candidates */}
					{extraction.instructions ? (
						<section className="prre-section">
							<h2 className="prre-section-title">
								<MessageSquareText size={18} className="prre-section-icon" />
								Instructions used
							</h2>
							<p className="prre-instructions">{extraction.instructions}</p>
						</section>
					) : null}

					{extraction.status === PlanReviewExtractionStatus.EXTRACTING ? (
						<div className="prre-running">
							<Loader2 className="prre-spinner" />
							<p className="prre-empty-title">Reading your guidelines…</p>
							<p>
								This takes a few minutes on a long document. You can leave this page — the rules will be waiting for you
								here.
							</p>
						</div>
					) : null}

					{extraction.status === PlanReviewExtractionStatus.FAILED ? (
						<div className="prre-empty">
							<AlertTriangle size={20} color="#e7000b" />
							<p className="prre-empty-title">The extraction failed</p>
							<p>{extraction.error_message ?? "Please try again."}</p>
						</div>
					) : null}

					{isReviewable ? (
						<>
							<div className="prre-summary">
								{[
									{ label: "Proposed", value: counts.total },
									{ label: "New", value: counts.new },
									{ label: "Changed", value: counts.changed },
									{ label: "Unchanged", value: counts.identical }
								].map(card => (
									<div key={card.label} className="prre-summary-card">
										<span className="prre-summary-label">{card.label}</span>
										<p className="prre-summary-value">{card.value}</p>
									</div>
								))}
							</div>

							{isCompleted ? (
								<div className="prre-card">
									This extraction has been reviewed and there is nothing left to import. Open the rules screen to see
									the imported rules in place.
								</div>
							) : null}

							{isCancelled ? (
								<div className="prre-card">
									This extraction was cancelled. Its rules are shown for reference only and can no longer be edited or
									imported.
								</div>
							) : null}

							<div className={`prre-candidates${isCancelled ? " prre-candidates-cancelled" : ""}`}>
								{candidates.map(candidate => (
									<PlanReviewCandidateCard
										key={candidate.id}
										candidate={candidate}
										submissionTypes={submissionTypes}
										categoryOptions={categoryOptions}
										readOnly={!canManage || isCancelled || !!candidate.imported_requirement_id}
										onFieldChange={handleFieldChange}
										onSubmissionTypesChange={handleTypesChange}
									/>
								))}
							</div>
						</>
					) : null}
				</div>
			</div>

			<LoadingOverlay isLoading={isImporting} tip="Importing rules…" />
		</OllieLayout>
	);
};

export default PlanReviewRulesExtraction;
